// Header row 1 team switcher. Click opens a dropdown listing teams the user
// is a member of, with "Create new team" at the bottom. Attention pip next to
// the team name when any project under it needs attention.

import { useAuthState } from 'react-firebase-hooks/auth'
import { useNavigate } from 'react-router-dom'
import { useMyTeams, useTeam } from '../../lib/data-hooks.js'
import { auth } from '../../lib/firebase.js'
import { AttentionPip } from './AttentionPip.jsx'
import { useDropdown } from './useDropdown.js'

export function TeamSwitcher({ teamId, attentionCount = 0 }) {
  const [user] = useAuthState(auth)
  const navigate = useNavigate()
  const { open, setOpen, ref } = useDropdown()
  const { data: teams } = useMyTeams(user?.uid)
  const { data: currentTeam } = useTeam(teamId)

  const label = currentTeam?.name ?? (teamId ? teamId : 'Select a team')

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 items-center gap-2 rounded-md px-2 text-sm text-[var(--color-basalt)] hover:bg-[var(--color-sandstone)]"
      >
        <span className="font-medium">{label}</span>
        <AttentionPip count={attentionCount} />
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-9 z-40 min-w-[220px] rounded-md border border-[var(--color-sandstone)] bg-[var(--color-limestone)] py-1 shadow-lg"
        >
          {teams.length === 0 && (
            <div className="px-3 py-2 text-xs text-[var(--color-schist)]">
              You're not on any teams yet.
            </div>
          )}
          {teams.map((t) => (
            <button
              key={t.teamId}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                navigate(`/teams/${t.teamId}`)
              }}
              className={`block w-full px-3 py-2 text-left text-sm hover:bg-[var(--color-sandstone)] ${
                t.teamId === teamId
                  ? 'font-medium text-[var(--color-basalt)]'
                  : 'text-[var(--color-slate)]'
              }`}
            >
              {t.teamId}
              <span className="ml-2 text-xs text-[var(--color-schist)]">{t.role}</span>
            </button>
          ))}
          <div className="my-1 border-t border-[var(--color-sandstone)]" />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              navigate('/teams?create=1')
            }}
            className="block w-full px-3 py-2 text-left text-sm text-[var(--color-terracotta)] hover:bg-[var(--color-sandstone)]"
          >
            Create new team
          </button>
        </div>
      )}
    </div>
  )
}
